import React from 'react';
import { Col, Form, Row } from 'antd';
import FiltersModal from 'components/new-ui/composed/modal/filters-modal/FiltersModal';
import SearchBar from 'components/new-ui/base/searchbar/SearchBar';
import CloseButton from 'components/new-ui/composed/modal/filters-modal/CloseButton';
import { ManifestStatus } from 'views/manifests/types/manifest-statuses';
import { ListManifestsParamKeys } from 'views/manifests/hooks/useManifestsFilters';
import TeamSelect from 'components/new-ui/composed/selects/TeamSelect';
import IndividualSelect from 'components/new-ui/composed/selects/IndividualSelect';
import ManifestStatusSelect from './ManifestStatusSelect';

export type ModalFilters = {
    query: string | undefined;
    manifestStatuses: ManifestStatus[];
    teamId: string | undefined;
    ownerId: string | undefined;
    createdBy: string | undefined;
};

type Props = {
    visible: boolean;
    filters: ModalFilters;
    optionalFilters?: {
        team?: boolean;
        assignedTo?: boolean;
    };
    onFiltersChange: (patch: Partial<ModalFilters>) => void;
    onClose: () => void;
    onClearAll: () => void;
    onClearFilter: (key: string, value?: string) => void;
};

const ManifestsFiltersModal: React.FC<Props> = ({
    visible,
    filters,
    optionalFilters,
    onFiltersChange,
    onClose,
    onClearAll,
    onClearFilter,
}) => {
    const { query, manifestStatuses, teamId, ownerId, createdBy } = filters;

    const handleClearFilters = () => {
        onClearAll();
    };

    return (
        <FiltersModal
            width={840}
            open={visible}
            footer={null}
            header={
                <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 16 }}>
                    <SearchBar
                        style={{ width: '100%' }}
                        inputPlaceholder="Search manifests"
                        onSearch={(val) => onFiltersChange({ query: val })}
                        value={query}
                    />
                    <CloseButton onClose={onClose} />
                </div>
            }
            onClearFilters={handleClearFilters}
            clearButtonDisabled={false}
            onClose={onClose}
            onSubmit={onClose}
        >
            <Form layout="vertical">
                <Row gutter={40}>
                    <Col span={12}>
                        <Form.Item label="Manifest Status">
                            <ManifestStatusSelect
                                value={manifestStatuses}
                                mode="multiple"
                                onChange={(val) => onFiltersChange({ manifestStatuses: val })}
                                placeholder="Select Statuses"
                            />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label="Created By">
                            <IndividualSelect
                                individualId={createdBy}
                                onChange={(id) => onFiltersChange({ createdBy: id })}
                                onClear={() => onClearFilter(ListManifestsParamKeys.CREATED_BY)}
                            />
                        </Form.Item>
                    </Col>

                    {optionalFilters?.assignedTo && (
                        <Col span={12}>
                            <Form.Item label="Assigned To">
                                <IndividualSelect
                                    individualId={ownerId}
                                    onChange={(id) => onFiltersChange({ ownerId: id })}
                                    onClear={() => onClearFilter(ListManifestsParamKeys.OWNER_ID)}
                                    filterIndividuals={(ind) => (ind.teamMemberships?.length ?? 0) > 0}
                                />
                            </Form.Item>
                        </Col>
                    )}

                    {optionalFilters?.team && (
                        <Col span={12}>
                            <Form.Item label="Team">
                                <TeamSelect
                                    teamId={teamId}
                                    onClear={() => onClearFilter(ListManifestsParamKeys.TEAM_ID)}
                                    onChange={(id) => onFiltersChange({ teamId: id })}
                                />
                            </Form.Item>
                        </Col>
                    )}
                </Row>
            </Form>
        </FiltersModal>
    );
};

export default ManifestsFiltersModal;
